"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { AvatarStack } from "@/components/AvatarStack";
import { CountBadge } from "@/components/UnreadMarker";
import { ConversationMenu } from "@/app/beskeder/ConversationMenu";
import { ConversationThread } from "@/app/beskeder/ConversationThread";
import { useConversation } from "@/lib/hooks/useConversation";
import {
  conversationTitle,
  otherParticipants,
  personName,
} from "@/app/beskeder/participants";
import { ESCAPE_LAYER, useEscapeLayer } from "@/lib/hooks/useEscapeLayer";

/** Most windows open at once. Beyond this the oldest is closed. */
const MAX_DOCKS = 3;
/** Gap the FeedbackButton keeps above whatever the dock occupies. */
const DOCK_GAP_PX = 12;
/**
 * How often a MINIMIZED window re-reads its conversation.
 *
 * Its unread badge is the only thing that tells the viewer a message arrived,
 * and the thread underneath — which is what polls — is unmounted while
 * minimized. `GET /conversations/{id}` is deliberately not pollable (§2.14:
 * the 1 Hz poll targets `/messages`), so this is a plain read on a slow tick
 * rather than a conditional one: fifteen seconds instead of the one second
 * the whole document used to be fetched at.
 */
const MINIMIZED_POLL_MS = 15_000;

interface DockState {
  id: number;
  minimized: boolean;
}

interface ChatDockValue {
  /** Open a conversation in the corner. No-op on mobile — see openConversation. */
  openChat: (id: number) => void;
  closeChat: (id: number) => void;
  /**
   * Open a conversation the right way for this viewport: a docked window on
   * desktop, a full-page navigation on mobile, where a 320px window in the
   * corner of a 375px screen would be absurd.
   */
  openConversation: (id: number) => void;
  /** Pixels the bottom-right corner is currently occupied for. */
  occupiedHeight: number;
}

const ChatDockContext = createContext<ChatDockValue | null>(null);

export function useChatDock(): ChatDockValue {
  const ctx = useContext(ChatDockContext);
  if (!ctx) {
    // Rendering outside the provider is a wiring mistake, but a messages link
    // should never crash the page over it.
    return {
      openChat: () => {},
      closeChat: () => {},
      openConversation: () => {},
      occupiedHeight: 0,
    };
  }
  return ctx;
}

export function ChatDockProvider({ children }: { children: React.ReactNode }) {
  const [docks, setDocks] = useState<DockState[]>([]);
  const router = useRouter();

  const closeChat = useCallback((id: number) => {
    setDocks((prev) => prev.filter((d) => d.id !== id));
  }, []);

  const openChat = useCallback((id: number) => {
    setDocks((prev) => {
      const existing = prev.find((d) => d.id === id);
      // Re-opening an already-docked conversation un-minimizes it rather than
      // adding a duplicate.
      if (existing) {
        return prev.map((d) => (d.id === id ? { ...d, minimized: false } : d));
      }
      const next = [...prev, { id, minimized: false }];
      return next.length > MAX_DOCKS ? next.slice(next.length - MAX_DOCKS) : next;
    });
  }, []);

  const openConversation = useCallback(
    (id: number) => {
      const isDesktop =
        typeof window !== "undefined" &&
        window.matchMedia("(min-width: 1024px)").matches;
      if (isDesktop) openChat(id);
      else router.push(`/beskeder/${id}`);
    },
    [openChat, router]
  );

  const toggleMinimize = useCallback((id: number) => {
    setDocks((prev) =>
      prev.map((d) => (d.id === id ? { ...d, minimized: !d.minimized } : d))
    );
  }, []);

  /**
   * Esc closes the front-most window — but ONLY when nothing is drawn over it.
   *
   * The dock is `z-40`, the lowest of the three layers, and its listener stays
   * registered for as long as any window is open, minimized included. Without
   * the rank gate it would consume the Escape meant for a composer opened
   * afterwards, or for the lightbox opened from a message inside one of its
   * own conversations. See `useEscapeLayer`.
   */
  const isTopmost = useEscapeLayer(docks.length > 0, ESCAPE_LAYER.dock);

  useEffect(() => {
    if (docks.length === 0) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // Not ours if a dropdown inside the window — emoji picker, reaction
      // summary — already answered it; see `useDismissable`.
      if (e.defaultPrevented || !isTopmost()) return;
      e.preventDefault();
      setDocks((prev) => prev.slice(0, -1));
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [docks.length, isTopmost]);

  /**
   * WHAT THE CORNER IS ACTUALLY OCCUPIED BY — MEASURED, NOT DECLARED.
   *
   * This used to add up constants: a `DOCK_HEIGHT_PX` kept in sync with the
   * window's height class by hand, or a `DOCK_HEADER_PX` when everything was
   * minimized. Two things break that, and OSO-446 makes both worse by
   * enlarging the window. The height class is now CLAMPED (`max-h`), which the
   * constant cannot see — so on a short screen the arithmetic over-reported
   * and the FeedbackButton was pushed past the top of the window; and once the
   * clamp binds, the constant under-reports the real height and the button
   * lands ON the chat window instead of above it.
   *
   * The row's own box is the answer to both, and it is free: one observer on
   * an element that is already here.
   */
  const rowRef = useRef<HTMLDivElement>(null);
  const [rowHeight, setRowHeight] = useState(0);

  useEffect(() => {
    const el = rowRef.current;
    if (!el) return;
    // `observe()` delivers an initial observation of its own, so there is no
    // first measurement to take synchronously here.
    const observer = new ResizeObserver(() =>
      setRowHeight(el.getBoundingClientRect().height),
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // `hidden lg:flex` — below `lg` the row is not laid out at all, and a corner
  // nothing occupies must not push the button up by the gap alone.
  const occupiedHeight =
    docks.length > 0 && rowHeight > 0 ? rowHeight + DOCK_GAP_PX : 0;

  const value = useMemo(
    () => ({ openChat, closeChat, openConversation, occupiedHeight }),
    [openChat, closeChat, openConversation, occupiedHeight]
  );

  return (
    <ChatDockContext.Provider value={value}>
      {children}
      {/* Desktop only: the dock stack grows leftward from the bottom-right.
          `left-6` is not decoration — it bounds the row to the viewport, which
          is what lets the windows SHRINK when three of the (now 1.5x wider)
          ones are open on a narrow laptop instead of running off the left
          edge. `justify-end` keeps them in the corner until they have to. */}
      <div
        ref={rowRef}
        className="hidden lg:flex fixed bottom-0 left-6 right-6 z-40 items-end justify-end gap-3 pointer-events-none"
      >
        {docks.map((dock) => (
          <ChatWindow
            key={dock.id}
            conversationId={dock.id}
            minimized={dock.minimized}
            onToggleMinimize={() => toggleMinimize(dock.id)}
            onClose={() => closeChat(dock.id)}
          />
        ))}
      </div>
    </ChatDockContext.Provider>
  );
}

function ChatWindow({
  conversationId,
  minimized,
  onToggleMinimize,
  onClose,
}: {
  conversationId: number;
  minimized: boolean;
  onToggleMinimize: () => void;
  onClose: () => void;
}) {
  const { data: conversation } = useConversation(
    conversationId,
    minimized ? { refetchMs: MINIMIZED_POLL_MS } : {}
  );
  const participants = conversation
    ? otherParticipants(conversation.participants)
    : [];
  const title = conversation
    ? conversationTitle(conversation, { firstNameOnly: true })
    : "…";
  const unread = conversation?.unreadCount ?? 0;

  return (
    <div
      /* 1.5x the old 320x420 (OSO-446). `w-[30rem]` is the width it asks for;
         `min-w-0` lets the flex row above take it back when three are open and
         the viewport is narrow, so the stack degrades to something like the
         old width rather than off-screen. `max-h` does the same for a short
         screen. */
      className={`pointer-events-auto w-[30rem] min-w-0 flex flex-col rounded-t-xl border border-b-0 border-border bg-surface shadow-2xl ${
        minimized ? "" : "h-[630px] max-h-[calc(100dvh-5.5rem)]"
      }`}
    >
      <div
        className="flex items-center gap-2 px-3 h-12 shrink-0 border-b border-border cursor-pointer rounded-t-xl hover:bg-surface-2/60 transition-colors"
        onClick={onToggleMinimize}
      >
        <AvatarStack
          primaryUrl={participants[0]?.avatar.small}
          primaryName={personName(participants[0])}
          otherUrls={participants.slice(1).map((u) => u.avatar.small)}
          otherNames={participants.slice(1).map(personName)}
        />
        <p className="flex-1 min-w-0 truncate text-sm font-semibold text-foreground">
          {title}
        </p>
        {conversation?.isGroup && (
          <span className="text-xs text-subtle-foreground shrink-0">
            · {conversation.participantCount}
          </span>
        )}
        {minimized && <CountBadge count={unread} label={`${unread} ulæste`} />}
        {/* Everything the page header offers — mute, add, block, report,
            delete, leave — so the tray is not a second-class place to be
            talked to. onGone closes the window itself: leaving, blocking or
            deleting a conversation while its window is docked must not leave
            it sitting open in the corner as if nothing happened. */}
        {conversation && (
          <div onClick={(e) => e.stopPropagation()}>
            <ConversationMenu conversation={conversation} compact onGone={onClose} />
          </div>
        )}
        <Link
          href={`/beskeder/${conversationId}`}
          onClick={(e) => e.stopPropagation()}
          aria-label="Åbn i fuld visning"
          title="Åbn i fuld visning"
          className="p-1 text-subtle-foreground hover:text-gray-700 dark:hover:text-gray-200 rounded transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 3 21 3 21 9" />
            <polyline points="9 21 3 21 3 15" />
            <line x1="21" y1="3" x2="14" y2="10" />
            <line x1="3" y1="21" x2="10" y2="14" />
          </svg>
        </Link>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggleMinimize();
          }}
          aria-label={minimized ? "Udvid samtale" : "Minimér samtale"}
          className="p-1 text-subtle-foreground hover:text-gray-700 dark:hover:text-gray-200 rounded transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            {minimized ? <polyline points="18 15 12 9 6 15" /> : <line x1="6" y1="12" x2="18" y2="12" />}
          </svg>
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          aria-label="Luk samtale"
          className="p-1 text-subtle-foreground hover:text-gray-700 dark:hover:text-gray-200 rounded transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Unmounted while minimized: a collapsed window must not keep polling
          at 1 Hz or mark the conversation read behind the user's back. */}
      {!minimized && (
        <ConversationThread conversationId={conversationId} compact />
      )}
    </div>
  );
}
